//React
import React from "react";
import PropTypes from "prop-types";
import MetricsBar from "../layout/metricsBar";
import { formatValue, valueLabel, smallBase, ratioBase } from "./formatters";
//CSS
import "./charts.scss";

/**
 * A `kpi` dataset (ASB-4377, design A.2): one tile per non-key column — a
 * column with `agg` or `ratio` set — aggregated over the range by the API, on
 * the V2 metrics bar. Values are compact, as on an axis, with the exact value
 * as the hover text. The API returns one row when the dataset has no group
 * keys; with group keys each row's tiles are labelled with its key values.
 *
 * A column's `help` (A.6) is the tile's info tip; a ratio under its `minBase`
 * appends the small-base note to it. `headline` names the column whose tile
 * is drawn larger (the panel's headline number, A.2).
 */
export function kpiMetrics(dataset, rows, { headline } = {}) {
	const columns = dataset.columns ?? [];
	const tiles = columns.filter((c) => c.agg || c.ratio);
	const keys = columns.filter((c) => !c.agg && !c.ratio);
	// Two tiles with one label (a count and its ratio both called "Flagged") would
	// share a React key on the metrics bar; the second carries its column key.
	const seen = new Set();
	const unique = (label, key) => {
		const out = seen.has(label) ? `${label} (${key})` : label;
		seen.add(out);
		return out;
	};
	return (rows ?? []).flatMap((row, i) => {
		const group = keys.map((k) => valueLabel(k, row[k.key], "cell")).join(" / ");
		return tiles.map((c) => {
			const value = row[c.key];
			const blank = value === null || value === undefined || value === "";
			const base = ratioBase(c, columns);
			const small = base && !blank ? smallBase(c, columns, row[base.key]) : null;
			const tip = [c.help, small?.note].filter(Boolean).join(" ");
			const metric = {
				key: `${c.key}:${i}`,
				label: unique(group ? `${c.label} · ${group}` : c.label, c.key),
				value: blank ? null : formatValue(c.type, value, "axis"),
			};
			if (tip) metric.tip = tip;
			if (blank && c.nullHelp) metric.valueTitle = c.nullHelp;
			else if (!blank) metric.valueTitle = formatValue(c.type, value, "cell");
			if (headline && c.key === headline) metric.headline = true;
			if (small) metric.muted = true;
			return metric;
		});
	});
}

function KpiStrip({ dataset, rows, headline }) {
	const metrics = kpiMetrics(dataset, rows, { headline });
	if (metrics.length === 0) {
		return <p className="as-chart__message">This dataset has no aggregated columns to show.</p>;
	}
	return (
		<MetricsBar
			metrics={metrics}
			className="as-kpi-strip"
		/>
	);
}

KpiStrip.propTypes = {
	/** a manifest dataset (design A.2) with chart "kpi" */
	dataset: PropTypes.shape({
		id: PropTypes.string,
		columns: PropTypes.array.isRequired,
	}).isRequired,
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	/** the column key drawn as the headline tile (panel `headline.column`) */
	headline: PropTypes.string,
};

export default KpiStrip;
